import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TiptapImage from "@tiptap/extension-image";
import { SeoPanel } from "../components/SeoPanel";
import { CommentsPanel } from "../components/CommentsPanel";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { ApproveModal } from "../components/ApproveModal";
import { ChangeMindDialog } from "../components/ChangeMindDialog";
import { useBrand, BrandLogo } from "../context/BrandContext";
import { GuidedTour } from "../components/GuidedTour";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";
import {
  ArrowLeft, Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered,
  Link2, Undo2, Redo2, Save, CheckCircle2, XCircle, MessageSquare, Image as ImageIcon, Lightbulb, X,
} from "lucide-react";

function ToolbarButton({ onClick, active, disabled, title, testId, children }) {
  return (
    <button
      type="button"
      data-testid={testId}
      title={title}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`toolbar-btn ${active ? "is-active" : ""} disabled:opacity-30`}
    >
      {children}
    </button>
  );
}

export default function EditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [title, setTitle] = useState("");
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectComments, setRejectComments] = useState("");
  const [changeMindOpen, setChangeMindOpen] = useState(false);
  const brand = useBrand();
  const off = (m) => (brand.disabled_modules || []).includes(m);
  const [commenting, setCommenting] = useState(false);
  const [showImageFeedback, setShowImageFeedback] = useState(false);
  const [imageFeedbackText, setImageFeedbackText] = useState("");
  const [imageMode, setImageMode] = useState("written");
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genImage, setGenImage] = useState(null);
  const [approvingImage, setApprovingImage] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [seoWarning, setSeoWarning] = useState(null);
  const seoWarnedRef = useRef({ heading: false });

  const locked = !!post?.approval;

  const editor = useEditor({
    extensions: [StarterKit, TiptapImage.configure({ draggable: false })],
    editorProps: { attributes: { spellcheck: "true" } },
    content: "",
    editable: true,
    onUpdate: () => setDirty(true),
    onSelectionUpdate: ({ editor }) => {
      if (editor.isEditable && editor.isActive("heading") && !seoWarnedRef.current.heading) {
        seoWarnedRef.current.heading = true;
        setSeoWarning("heading");
      }
    },
  });

  useEffect(() => {
    api
      .get(`/posts/${id}`)
      .then((res) => {
        setPost(res.data);
        setTitle(res.data.title);
      })
      .catch((err) => setError(apiError(err, "Failed to load the article")));
  }, [id]);

  useEffect(() => {
    if (editor && post) {
      editor.commands.setContent(post.content);
      editor.setEditable(!post.approval);
      setDirty(false);
    }
  }, [editor, post]);

  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/posts/${id}/comments`);
        setPost((p) => (p ? { ...p, comments: data.comments } : p));
      } catch (e) {
        /* ignore polling errors */
      }
    }, 12000);
    return () => clearInterval(iv);
  }, [id]);

  const save = useCallback(async () => {
    if (!editor || saving) return;
    setSaving(true);
    try {
      await api.put(`/posts/${id}`, { title, content: editor.getHTML() });
      setDirty(false);
      toast.success("Changes saved to draft");
    } catch (err) {
      toast.error(apiError(err, "Failed to save changes"));
    } finally {
      setSaving(false);
    }
  }, [editor, saving, id, title]);

  const approve = async (payload) => {
    setApproving(true);
    try {
      if (dirty && editor) {
        await api.put(`/posts/${id}`, { title, content: editor.getHTML() });
        setDirty(false);
      }
      const { data } = await api.post(`/posts/${id}/approve`, {
        ...payload,
        confirmed: true,
      });
      setPost((p) => ({ ...p, approval: data }));
      editor?.setEditable(false);
      setApproveOpen(false);
      toast.success("Article approved — our team has been notified");
    } catch (err) {
      toast.error(apiError(err, "Failed to approve the article"));
    } finally {
      setApproving(false);
    }
  };

  const reject = async () => {
    setRejecting(true);
    try {
      const { data } = await api.post(`/posts/${id}/reject`, { comments: rejectComments });
      setPost((p) => ({ ...p, approval: data }));
      editor?.setEditable(false);
      setRejectOpen(false);
      toast.success("Article rejected — our team has been notified");
    } catch (err) {
      toast.error(apiError(err, "Failed to reject the article"));
    } finally {
      setRejecting(false);
    }
  };

  const addComment = async (text, target = "general") => {
    setCommenting(true);
    try {
      const { data } = await api.post(`/posts/${id}/comments`, { text, target });
      setPost((p) => ({ ...p, comments: [...(p.comments || []), data] }));
      toast.success(`Feedback sent to the ${brand.display_name} team`);
    } catch (err) {
      toast.error(apiError(err, "Failed to send feedback"));
    } finally {
      setCommenting(false);
    }
  };

  const defaultAiPrompt = () => {
    const excerpt = (post?.content || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 220);
    return `Photorealistic editorial photograph for a blog article titled "${title}". Visually represent the article's topic: ${excerpt}. Professional photography, natural lighting, wide 16:9 blog header composition. Strictly no text, words, letters, watermarks, or logos in the image.`;
  };

  const generateImage = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/posts/${id}/generate-image`, { prompt: aiPrompt });
      setGenImage(data);
    } catch (err) {
      toast.error(apiError(err, "Image generation failed"));
    } finally {
      setGenerating(false);
    }
  };

  const approveGenImage = async () => {
    setApprovingImage(true);
    try {
      const { data } = await api.post(`/posts/${id}/approve-image`, { image_id: genImage.image_id });
      setPost((p) => ({ ...p, comments: [...(p.comments || []), data.comment] }));
      setGenImage(null);
      setShowImageFeedback(false);
      toast.success("AI image approved — sent to our team");
    } catch (err) {
      toast.error(apiError(err, "Failed to approve the image"));
    } finally {
      setApprovingImage(false);
    }
  };

  const setLink = () => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  if (error)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAF9] gap-4">
        <p data-testid="editor-error-message" className="text-red-600">{error}</p>
        <Button variant="outline" className="rounded-full" onClick={() => navigate("/")}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to queue
        </Button>
      </div>
    );

  if (!post)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="im-spinner" />
      </div>
    );

  const imageComments = (post?.comments || []).filter((c) => c.target === "image");
  const generalComments = (post?.comments || []).filter((c) => c.target !== "image" && c.target !== "decision");
  const decisionComments = (post?.comments || []).filter((c) => c.target === "decision");

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="border-b border-black/[0.08] bg-white/80 backdrop-blur-md sticky top-0 z-40">
        <div className="px-4 sm:px-8 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-to-queue-button"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="rounded-full text-neutral-600"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Queue
            </Button>
            <BrandLogo className="h-8 w-auto hidden sm:block" textClass="text-xl hidden sm:block" testId="brand-logo-editor" />
          </div>

          <div className="flex items-center gap-3">
            {locked ? (
              <>
              {post.approval.status === "rejected" ? (
                <Badge
                  data-testid="rejected-status-badge"
                  className="rounded-full bg-red-50 text-red-700 border border-red-200 hover:bg-red-50 gap-1"
                >
                  <XCircle className="h-3 w-3" /> Rejected
                </Badge>
              ) : (
                <Badge
                  data-testid="approved-status-badge"
                  className="rounded-full bg-green-50 text-green-700 border border-green-200 hover:bg-green-50 gap-1"
                >
                  <CheckCircle2 className="h-3 w-3" /> Approved by {post.approval.signature_name}
                </Badge>
              )}
              {!off("change-mind") && (
              <Button
                data-testid="change-mind-button"
                variant="outline"
                size="sm"
                onClick={() => setChangeMindOpen(true)}
                className="rounded-full border-black/10 active:scale-95"
              >
                <MessageSquare className="h-3.5 w-3.5 mr-1.5" /> Changed your mind?
              </Button>
              )}
              </>
            ) : (
              <>
                <span className="hidden sm:inline text-xs text-neutral-400 font-mono-accent">
                  {dirty ? "Unsaved changes" : "All changes saved"}
                </span>
                <Button
                  data-testid="save-changes-button"
                  variant="outline"
                  size="sm"
                  onClick={save}
                  disabled={saving || !dirty}
                  className="rounded-full border-black/10 active:scale-95"
                >
                  <Save className="h-3.5 w-3.5 mr-1.5" /> {saving ? "Saving..." : "Save"}
                </Button>
                <Button
                  data-testid="open-reject-dialog-button"
                  size="sm"
                  onClick={() => setRejectOpen(true)}
                  className="rounded-full bg-red-600 hover:bg-red-700 active:scale-95 transition-colors text-white"
                >
                  <XCircle className="h-3.5 w-3.5 mr-1.5" /> Reject
                </Button>
                <Button
                  data-testid="open-approve-modal-button"
                  size="sm"
                  onClick={() => setApproveOpen(true)}
                  className="rounded-full bg-green-600 hover:bg-green-700 active:scale-95 transition-colors text-white"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve
                </Button>
              </>
            )}
          </div>
        </div>

        {!locked && editor && (
          <div className="border-t border-black/[0.06] bg-white">
            <div className="px-4 sm:px-8 h-11 flex items-center gap-1 overflow-x-auto">
              <ToolbarButton testId="toolbar-bold" title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
                <Bold className="h-4 w-4" />
              </ToolbarButton>
              <ToolbarButton testId="toolbar-italic" title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
                <Italic className="h-4 w-4" />
              </ToolbarButton>
              <ToolbarButton testId="toolbar-underline" title="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline?.().run?.() ?? null}>
                <Underline className="h-4 w-4" />
              </ToolbarButton>
              <div className="w-px h-5 bg-black/10 mx-1.5" />
              <ToolbarButton testId="toolbar-h2" title="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
                <Heading2 className="h-4 w-4" />
              </ToolbarButton>
              <ToolbarButton testId="toolbar-h3" title="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
                <Heading3 className="h-4 w-4" />
              </ToolbarButton>
              <div className="w-px h-5 bg-black/10 mx-1.5" />
              <ToolbarButton testId="toolbar-bullet-list" title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
                <List className="h-4 w-4" />
              </ToolbarButton>
              <ToolbarButton testId="toolbar-ordered-list" title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                <ListOrdered className="h-4 w-4" />
              </ToolbarButton>
              <ToolbarButton testId="toolbar-link" title="Link" active={editor.isActive("link")} onClick={setLink}>
                <Link2 className="h-4 w-4" />
              </ToolbarButton>
              <div className="w-px h-5 bg-black/10 mx-1.5" />
              <ToolbarButton testId="toolbar-undo" title="Undo" onClick={() => editor.chain().focus().undo().run()}>
                <Undo2 className="h-4 w-4" />
              </ToolbarButton>
              <ToolbarButton testId="toolbar-redo" title="Redo" onClick={() => editor.chain().focus().redo().run()}>
                <Redo2 className="h-4 w-4" />
              </ToolbarButton>
            </div>
          </div>
        )}
      </header>

      <main className="px-4 sm:px-8 mt-6 mb-20 fade-in">
        {!locked && (
          <div className="mb-4 flex flex-wrap items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5" data-testid="edit-instructions-banner">
            <Lightbulb className="h-4 w-4 text-amber-500 shrink-0" />
            <span className="text-sm text-amber-900">
              You can edit this article directly — click into the title or text and type. When you're happy with it, hit <strong>Approve</strong>.
            </span>
            <button
              data-testid="start-tour-button"
              onClick={() => setTourOpen(true)}
              className="ml-auto text-xs font-semibold text-amber-700 border border-amber-300 rounded-full px-3 py-1 hover:bg-amber-100 transition-colors active:scale-95 whitespace-nowrap"
            >
              Show me how
            </button>
          </div>
        )}
        <div className="flex flex-col xl:flex-row gap-6 items-start">
        <div className="xl:hidden w-full">
          {!off("seo-panel") && <SeoPanel seo={post.seo} compact />}
        </div>
        <div className="flex-1 w-full bg-white border border-black/[0.08] rounded-xl shadow-[0_2px_12px_rgba(0,0,0,0.04)] py-10 px-8 sm:px-12 min-h-[80vh]">
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-neutral-400 mb-1">
            Draft article &middot; {post.word_count} words
          </p>
          {post.review_request?.requested_at ? (
            <p data-testid="review-request-info" className="text-xs text-neutral-500 mb-4">
              Review requested by {post.review_request.requested_by || `the ${brand.display_name} team`} on{" "}
              {new Date(post.review_request.requested_at).toLocaleDateString()}
            </p>
          ) : (
            <div className="mb-4" />
          )}
          {post.featured_image?.url && (
            <div className="mb-6" data-testid="featured-image-wrapper">
              <div className="flex flex-col sm:flex-row sm:items-stretch gap-4">
                <motion.div
                  animate={{ width: showImageFeedback && !locked ? "58%" : "100%" }}
                  transition={{ duration: 0.35, ease: "easeInOut" }}
                  className="relative shrink-0 hidden sm:block"
                >
                  <img
                    src={post.featured_image.url}
                    alt={post.featured_image.alt || ""}
                    className="w-full h-auto rounded-lg"
                    data-testid="featured-image"
                  />
                  <span className="absolute top-3 left-3 text-[10px] font-mono-accent uppercase tracking-[0.15em] bg-black/60 text-white px-2.5 py-1 rounded-full">
                    Featured image &middot; view only
                  </span>
                  {!locked && !showImageFeedback && (
                    <button
                      type="button"
                      data-testid="comment-on-image-button"
                      onClick={() => setShowImageFeedback(true)}
                      className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white/90 backdrop-blur border border-black/10 text-neutral-700 hover:bg-white transition-colors active:scale-95 whitespace-nowrap"
                    >
                      <MessageSquare className="h-3.5 w-3.5" /> Click to request image change
                    </button>
                  )}
                </motion.div>
                <div className="relative sm:hidden">
                  <img src={post.featured_image.url} alt={post.featured_image.alt || ""} className="w-full h-auto rounded-lg" />
                  {!locked && !showImageFeedback && (
                    <button
                      type="button"
                      onClick={() => setShowImageFeedback(true)}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-black/10 text-neutral-600"
                    >
                      <MessageSquare className="h-3.5 w-3.5" /> Click to request image change
                    </button>
                  )}
                </div>

                {showImageFeedback && !locked && (
                  <motion.div
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.25 }}
                    className="flex-1 min-w-[240px] bg-neutral-50 border border-black/[0.08] rounded-lg p-4 flex flex-col"
                    data-testid="image-feedback-form"
                  >
                    {!off("ai-images") && (
                    <div className="flex gap-2 mb-3">
                      <button
                        type="button"
                        data-testid="image-mode-written"
                        onClick={() => setImageMode("written")}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors active:scale-95 ${
                          imageMode === "written"
                            ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]"
                            : "border-black/10 text-neutral-600 hover:bg-black/[0.04]"
                        }`}
                      >
                        Written request
                      </button>
                      <button
                        type="button"
                        data-testid="image-mode-ai"
                        onClick={() => {
                          setImageMode("ai");
                          if (!aiPrompt) setAiPrompt(defaultAiPrompt());
                        }}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors active:scale-95 ${
                          imageMode === "ai"
                            ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]"
                            : "border-black/10 text-neutral-600 hover:bg-black/[0.04]"
                        }`}
                      >
                        AI image
                      </button>
                    </div>
                    )}

                    {imageMode === "written" ? (
                      <>
                        <p className="font-mono-accent text-[10px] uppercase tracking-[0.18em] text-neutral-400 mb-2">
                          Request an image change
                        </p>
                        <textarea
                          data-testid="image-feedback-input"
                          value={imageFeedbackText}
                          onChange={(e) => setImageFeedbackText(e.target.value)}
                          placeholder="Tell us what you'd rather see instead..."
                          className="w-full flex-1 min-h-[80px] text-sm border border-black/10 rounded-lg p-3 outline-none focus:ring-2 focus:ring-[#1058A8]/40 resize-none bg-white"
                        />
                        <div className="flex gap-2 mt-3">
                          <Button
                            data-testid="image-feedback-submit-button"
                            size="sm"
                            disabled={!imageFeedbackText.trim() || commenting}
                            onClick={async () => {
                              await addComment(imageFeedbackText.trim(), "image");
                              setImageFeedbackText("");
                              setShowImageFeedback(false);
                            }}
                            className="rounded-full bg-[var(--brand-primary)] hover:opacity-90 text-white active:scale-95 transition-colors"
                          >
                            {commenting ? "Sending..." : "Send request"}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid="image-feedback-cancel-button"
                            onClick={() => setShowImageFeedback(false)}
                            className="rounded-full text-neutral-500"
                          >
                            Cancel
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="font-mono-accent text-[10px] uppercase tracking-[0.18em] text-neutral-400 mb-2">
                          AI image prompt — edit or replace it
                        </p>
                        <textarea
                          data-testid="ai-prompt-input"
                          value={aiPrompt}
                          onChange={(e) => setAiPrompt(e.target.value)}
                          className="w-full flex-1 min-h-[90px] text-xs leading-relaxed border border-black/10 rounded-lg p-3 outline-none focus:ring-2 focus:ring-[#1058A8]/40 resize-none bg-white"
                        />
                        {genImage && (
                          <img
                            data-testid="ai-generated-preview"
                            src={`${process.env.REACT_APP_BACKEND_URL}${genImage.url}`}
                            alt="AI generated preview"
                            className="w-full h-auto rounded-lg mt-3 border border-black/[0.08]"
                          />
                        )}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {!genImage ? (
                            <Button
                              data-testid="ai-generate-button"
                              size="sm"
                              disabled={!aiPrompt.trim() || generating}
                              onClick={generateImage}
                              className="rounded-full bg-[var(--brand-primary)] hover:opacity-90 text-white active:scale-95 transition-colors"
                            >
                              {generating ? "Generating (up to 30s)..." : "Generate image"}
                            </Button>
                          ) : (
                            <>
                              <Button
                                data-testid="ai-image-approve-button"
                                size="sm"
                                disabled={approvingImage}
                                onClick={approveGenImage}
                                className="rounded-full bg-green-600 hover:bg-green-700 text-white active:scale-95 transition-colors"
                              >
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                                {approvingImage ? "Approving..." : "Approve this image"}
                              </Button>
                              <Button
                                data-testid="ai-image-regenerate-button"
                                size="sm"
                                variant="outline"
                                disabled={generating}
                                onClick={generateImage}
                                className="rounded-full border-black/10 active:scale-95"
                              >
                                {generating ? "Generating..." : "Regenerate"}
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid="ai-image-cancel-button"
                            onClick={() => {
                              setGenImage(null);
                              setShowImageFeedback(false);
                            }}
                            className="rounded-full text-neutral-500"
                          >
                            Cancel
                          </Button>
                        </div>
                      </>
                    )}
                  </motion.div>
                )}
              </div>
              {imageComments.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="image-comments-list">
                  {imageComments.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-start gap-2 text-xs text-neutral-600 bg-neutral-50 border border-black/[0.05] rounded-lg px-3 py-2"
                    >
                      <ImageIcon className="h-3.5 w-3.5 mt-0.5 text-neutral-400 shrink-0" />
                      <div>
                        <span className="font-semibold text-neutral-700">{c.author_name}:</span> {c.text}
                        {c.image_url && (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${c.image_url}`}
                            alt="Approved AI replacement"
                            className="mt-2 rounded-md w-full max-w-sm border border-black/[0.08]"
                          />
                        )}
                        <span className="block font-mono-accent text-[9px] text-neutral-400 mt-0.5">
                          {new Date(c.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <textarea
            data-testid="article-title-input"
            spellCheck="true"
            onClick={() => {
              if (!locked) setSeoWarning("title");
            }}
            className="article-title-input text-2xl sm:text-3xl mb-6"
            value={title}
            rows={2}
            readOnly={locked}
            onChange={(e) => {
              setTitle(e.target.value);
              setDirty(true);
            }}
          />
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-neutral-400 mb-2 mt-2" data-testid="editor-label">
            {locked ? "Article text · read only" : "Article text · click below to edit"}
          </p>
          <div className="article-canvas" data-testid="article-body-editor">
            <EditorContent editor={editor} />
          </div>

          {locked && (
            <div className="mt-12 pt-8 border-t border-black/[0.06]">
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-neutral-400 mb-2">
                {post.approval.status === "rejected" ? "Rejection record" : "Approval record"}
              </p>
              {post.approval.signature_name && (
                <p className="font-article italic text-xl text-neutral-800">{post.approval.signature_name}</p>
              )}
              <p className="font-mono-accent text-xs text-neutral-500 mt-1">
                {post.approval.status === "rejected" ? `Rejected by ${post.approval.signed_by_username}` : "Signed"}{" "}
                {new Date(post.approval.signed_at).toLocaleString()}
              </p>
            </div>
          )}
        </div>

        <aside className="w-full xl:w-[340px] shrink-0 space-y-6 xl:sticky xl:top-28">
          <div className="hidden xl:block">
            {!off("seo-panel") && <SeoPanel seo={post.seo} />}
          </div>
          {!off("chat") && <CommentsPanel
            comments={generalComments}
            currentUser={user?.username}
            onSubmit={(text) => addComment(text, "general")}
            submitting={commenting}
            locked={locked}
          />}
        </aside>
        </div>
      </main>

      <ApproveModal
        open={approveOpen}
        onOpenChange={setApproveOpen}
        postTitle={title}
        onApprove={approve}
        submitting={approving}
      />
      <GuidedTour open={tourOpen} onClose={() => setTourOpen(false)} />
      <ChangeMindDialog
        open={changeMindOpen}
        onOpenChange={setChangeMindOpen}
        status={post.approval?.status}
        comments={decisionComments}
        currentUser={user?.username}
        onSubmit={(text) => addComment(text, "decision")}
        submitting={commenting}
      />
      <AlertDialog
        open={!!seoWarning}
        onOpenChange={(v) => {
          if (!v) setSeoWarning(null);
        }}
      >
        <AlertDialogContent className="rounded-xl" data-testid="seo-warning-dialog">
          <button
            type="button"
            data-testid="seo-warning-close-x"
            onClick={() => setSeoWarning(null)}
            className="absolute right-4 top-4 rounded-full p-1 text-neutral-400 hover:text-neutral-700 hover:bg-black/[0.05] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl font-black tracking-tighter flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" /> A quick word about SEO
            </AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-600 leading-relaxed">
              {seoWarning === "title"
                ? "Editing the headline can have a significant impact on this article's search optimization score. Small fixes are fine — but for bigger headline changes, consider leaving feedback for our team so we can keep the SEO intact."
                : "Editing section headings (H2/H3) can have a significant impact on this article's search optimization score. Small fixes are fine — but for bigger heading changes, consider leaving feedback for our team so we can keep the SEO intact."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {seoWarning === "title" && (
              <AlertDialogCancel
                data-testid="seo-warning-cancel-edits-button"
                onClick={() => {
                  setTitle(post.title);
                  setSeoWarning(null);
                }}
                className="rounded-full"
              >
                Cancel edits
              </AlertDialogCancel>
            )}
            <AlertDialogAction
              data-testid="seo-warning-dismiss-button"
              onClick={() => setSeoWarning(null)}
              className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
            >
              Got it — I'll edit carefully
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent className="rounded-xl" data-testid="reject-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl font-black tracking-tighter flex items-center gap-2">
              <XCircle className="h-6 w-6 text-red-600" /> Reject this article?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-600 leading-relaxed">
              Are you sure you wish to cancel this blog post entirely? If you want to make changes, you
              can make them directly in the article, or if you want to leave feedback, we can make some
              changes for you based on the feedback.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="pt-1">
            <Textarea
              data-testid="reject-comments-input"
              value={rejectComments}
              onChange={(e) => setRejectComments(e.target.value)}
              placeholder="Optional — tell our team why, or what you'd like changed instead"
              className="rounded-lg min-h-[80px]"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reject-cancel-button" className="rounded-full">
              Keep reviewing
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="reject-confirm-button"
              disabled={rejecting}
              onClick={(e) => {
                e.preventDefault();
                reject();
              }}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white"
            >
              {rejecting ? "Rejecting..." : "Yes, scrap this article"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
